import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    CalendarIcon,
    LocationIcon,
    TimeIcon,
    InfoIcon,
    CloseIcon,
    DownloadIcon,
    ExternalLinkIcon,
    ArticleIcon,
    ShareIcon,
    ArrowRightIcon,
    EditIcon as Edit2
} from './Icons';
import InlineFieldEditor, { EditButton } from './InlineFieldEditor';
import { downloadIcsFile, getGoogleCalendarUrl } from '../utils/calendarExport';
import { ensureAbsoluteUrl } from '../utils/urlHelper';
import {
    Card,
    CardContent,
    Typography,
    Chip,
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stack,
    IconButton,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    useTheme,
    alpha
} from '@mui/material';

const EventCard = ({ event, isEditable = false, onUpdate = () => {} }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [editingField, setEditingField] = useState(null);
    const [calendarAnchorEl, setCalendarAnchorEl] = useState(null);
    const navigate = useNavigate();
    const theme = useTheme();

    const getCategoryStyles = (category) => {
        switch (category) {
            case 'Lager':
                return {
                    badgeClass: 'bg-tertiary-container text-tertiary-onContainer border border-tertiary/25',
                    colorVar: 'rgb(var(--color-tertiary))',
                    containerVar: 'rgb(var(--color-tertiary-container))',
                    onContainerVar: 'rgb(var(--color-tertiary-on-container))',
                    onVar: 'rgb(var(--color-tertiary-on))',
                    muiColor: 'warning',
                };
            case 'Aktion':
                return {
                    badgeClass: 'bg-secondary-container text-secondary-onContainer border border-secondary/25',
                    colorVar: 'rgb(var(--color-secondary))',
                    containerVar: 'rgb(var(--color-secondary-container))',
                    onContainerVar: 'rgb(var(--color-secondary-on-container))',
                    onVar: 'rgb(var(--color-secondary-on))',
                    muiColor: 'secondary',
                };
            case 'Gruppenstunde':
                return {
                    badgeClass: 'bg-primary-container text-primary-onContainer border border-primary/25',
                    colorVar: 'rgb(var(--color-primary))',
                    containerVar: 'rgb(var(--color-primary-container))',
                    onContainerVar: 'rgb(var(--color-primary-on-container))',
                    onVar: 'rgb(var(--color-primary-on))',
                    muiColor: 'primary',
                };
            default:
                return {
                    badgeClass: 'bg-surface-containerHighest text-surface-onVariant border border-outlineVariant/30',
                    colorVar: 'rgb(var(--color-primary))',
                    containerVar: 'rgb(var(--color-surface-container-high))',
                    onContainerVar: 'rgb(var(--color-surface-on))',
                    onVar: 'rgb(var(--color-primary-on))',
                    muiColor: 'primary',
                };
        }
    };

    const getCategoryColor = (category) => {
        switch (category) {
            case 'Gruppenstunde': return 'success';
            case 'Lager': return 'warning';
            case 'Aktion': return 'secondary';
            default: return 'primary';
        }
    };

    const isMultiDay = Boolean(
        event.endDate &&
        event.date &&
        new Date(event.endDate).toDateString() !== new Date(event.date).toDateString()
    );

    const formatDate = (dateString, endDateString) => {
        if (!dateString) return '—';
        const start = new Date(dateString);
        if (!endDateString || dateString === endDateString || (endDateString && new Date(dateString).toDateString() === new Date(endDateString).toDateString())) {
            const options = { weekday: 'short', year: 'numeric', month: 'long', day: 'numeric' };
            return start.toLocaleDateString('de-DE', options);
        }
        const end = new Date(endDateString);
        const sameYear = start.getFullYear() === end.getFullYear();
        const sameMonth = sameYear && start.getMonth() === end.getMonth();
        if (sameMonth) {
            return `${start.toLocaleDateString('de-DE', { weekday: 'short', day: 'numeric' })}. – ${end.toLocaleDateString('de-DE', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' })}`;
        }
        return `${start.toLocaleDateString('de-DE', { day: 'numeric', month: 'short' })} – ${end.toLocaleDateString('de-DE', { day: 'numeric', month: 'short', year: 'numeric' })}`;
    };

    const categoryStyles = getCategoryStyles(event.category);
    const categoryColor = categoryStyles.muiColor;

    const handleUpdateField = (field, value) => {
        onUpdate(field, value);
    };

    const isPast = (event.endDate ? new Date(event.endDate) : (event.date ? new Date(event.date) : new Date(0))).setHours(23, 59, 59, 999) < Date.now();

    const getExternalButtonLabel = () => {
        if (event.linkText) return event.linkText;
        if (event.linkType === 'info') return isPast ? 'Mehr Infos' : 'Mehr Infos';
        return isPast ? 'Mehr Infos' : 'Zur Anmeldung';
    };

    const getInternalButtonLabel = () => {
        if (isPast) {
            return event.linkedBlogs && event.linkedBlogs.length > 0 ? 'Rückblick & Bericht' : 'Rückblick & Details';
        }
        if (event.isRegistrationEnabled) {
            return 'Details & Anmeldung';
        }
        return 'Details ansehen';
    };

    const handleCardClick = () => {
        if (event.slug) {
            navigate(`/events/${event.slug}`);
        } else if (event._id) {
            navigate(`/events/${event._id}`);
        } else if (event.link) {
            window.open(ensureAbsoluteUrl(event.link), '_blank', 'noopener,noreferrer');
        } else {
            setIsExpanded(true);
        }
    };

    const handleCalendarClick = (e) => {
        e.stopPropagation();
        setCalendarAnchorEl(e.currentTarget);
    };

    const handleCalendarClose = (e) => {
        if (e) e.stopPropagation();
        setCalendarAnchorEl(null);
    };

    const handleDownloadIcs = (e) => {
        e.stopPropagation();
        downloadIcsFile(event);
        handleCalendarClose();
    };

    const handleOpenGoogleCal = (e) => {
        e.stopPropagation();
        const url = getGoogleCalendarUrl(event);
        window.open(url, '_blank', 'noopener,noreferrer');
        handleCalendarClose();
    };

    return (
        <>
            <Card
                sx={{
                    flex: 1,
                    height: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: '24px',
                    cursor: 'pointer',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    backgroundColor: 'rgb(var(--color-surface-container-lowest))',
                    color: 'rgb(var(--color-surface-on))',
                    position: 'relative',
                    overflow: 'hidden',
                    border: '1px solid rgb(var(--color-outline-variant))',
                    boxShadow: 'none',
                    '.dark &': {
                        backgroundColor: 'rgb(var(--color-surface-container)) !important',
                        border: '1px solid rgb(var(--color-outline-variant)) !important',
                        boxShadow: 'none'
                    },
                    '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 'none',
                        backgroundColor: 'rgb(var(--color-surface-container-low))',
                        borderColor: 'rgb(var(--color-primary)) !important',
                        '.dark &': {
                            backgroundColor: 'rgb(var(--color-surface-container-high)) !important',
                            borderColor: 'rgb(var(--color-primary)) !important',
                            boxShadow: 'none'
                        }
                    },
                }}
                onClick={handleCardClick}
            >
                <CardContent sx={{ flexGrow: 1, p: { xs: 2.5, sm: 3 }, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 1 }}>
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className={`px-2.5 py-0.5 rounded-lg text-[11px] font-bold uppercase tracking-wider ${categoryStyles.badgeClass}`}>
                                {event.category || 'Event'}
                            </span>
                            {event.linkedGroup && (
                                <span
                                    className="px-2.5 py-0.5 rounded-lg text-[11px] font-bold tracking-wider truncate max-w-[140px]"
                                    style={{
                                        backgroundColor: `${event.linkedGroup.color || '#4CAF50'}20`,
                                        color: event.linkedGroup.color || '#4CAF50',
                                        borderColor: `${event.linkedGroup.color || '#4CAF50'}50`,
                                        borderWidth: 1,
                                        borderStyle: 'solid'
                                    }}
                                >
                                    {event.linkedGroup.name}
                                </span>
                            )}
                            {isPast && (
                                <span className="px-2 py-0.5 rounded-lg bg-surface-containerHighest text-surface-onVariant text-[10px] font-bold">
                                    Vergangen
                                </span>
                            )}
                            {isEditable && <EditButton onClick={() => setEditingField('category')} />}
                        </div>

                        <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                            {event.isRegistrationEnabled && !isPast && (
                                <span className="px-2.5 py-1 rounded-lg bg-primary-container text-primary-onContainer border border-primary/25 text-[10px] font-bold tracking-wide whitespace-nowrap">
                                    Anmeldung offen
                                </span>
                            )}

                            {/* Add to Calendar quick button */}
                            {!isPast && (
                                <IconButton
                                    size="small"
                                    onClick={handleCalendarClick}
                                    title="Zu Kalender hinzufügen"
                                    aria-label="Zu Kalender hinzufügen"
                                    sx={{
                                        color: 'rgba(var(--color-surface-on-variant), 0.7)',
                                        bgcolor: 'rgba(var(--color-surface-container-high), 0.8)',
                                        '&:hover': {
                                            color: categoryStyles.colorVar,
                                            bgcolor: categoryStyles.containerVar
                                        }
                                    }}
                                >
                                    <CalendarIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                            )}

                            {event.link && (
                                <IconButton
                                    size="small"
                                    href={ensureAbsoluteUrl(event.link)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    title="Externer Link / Anmeldung"
                                    sx={{
                                        color: 'rgba(var(--color-surface-on-variant), 0.6)',
                                        '&:hover': {
                                            color: categoryStyles.colorVar,
                                            bgcolor: categoryStyles.containerVar
                                        }
                                    }}
                                >
                                    <InfoIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                            )}
                        </div>
                    </Box>

                    {isEditable && editingField === 'title' ? (
                        <div className="mb-4" onClick={(e) => e.stopPropagation()}>
                            <InlineFieldEditor 
                                label="Titel" 
                                value={event.title} 
                                onSave={(val) => { handleUpdateField('title', val); setEditingField(null); }}
                                onCancel={() => setEditingField(null)}
                            />
                        </div>
                    ) : (
                        <Typography
                            variant="h6"
                            component="h3"
                            sx={{
                                fontWeight: 700,
                                mb: 2,
                                minHeight: '2.8em',
                                lineHeight: 1.3,
                                color: 'rgba(var(--color-surface-on), 1)',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden'
                            }}
                        >
                            {event.title}
                            {isEditable && <EditButton onClick={() => setEditingField('title')} />}
                        </Typography>
                    )}

                    {event.description && (
                        <Box sx={{ mb: 1.5 }}>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'rgba(var(--color-surface-on-variant), 0.85)',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    fontSize: '0.84rem',
                                    lineHeight: 1.45
                                }}
                            >
                                {event.description}
                            </Typography>
                        </Box>
                    )}

                    <Stack spacing={1.5} sx={{ mt: 'auto' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: 'rgba(var(--color-surface-on-variant), 0.85)' }}>
                            <Box sx={{ bgcolor: categoryStyles.containerVar, color: categoryStyles.onContainerVar, p: 0.7, borderRadius: 1.5, display: 'flex' }}>
                                <CalendarIcon sx={{ fontSize: 16, color: 'inherit' }} />
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatDate(event.date, event.endDate)}</Typography>
                            {isEditable && <EditButton onClick={() => setEditingField('date')} />}
                        </Box>

                        {isEditable && editingField === 'date' && (
                            <div className="my-2" onClick={(e) => e.stopPropagation()}>
                                <InlineFieldEditor 
                                    label="Datum" 
                                    type="date"
                                    value={event.date?.split('T')[0]} 
                                    onSave={(val) => { handleUpdateField('date', val); setEditingField(null); }}
                                    onCancel={() => setEditingField(null)}
                                />
                            </div>
                        )}

                        {!isMultiDay && (event.startTime || event.endTime || isEditable) && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: 'rgba(var(--color-surface-on-variant), 0.85)' }}>
                                <Box sx={{ bgcolor: 'rgba(var(--color-surface-on-variant), 0.08)', p: 0.7, borderRadius: 1.5, display: 'flex' }}>
                                    <TimeIcon sx={{ fontSize: 16 }} />
                                </Box>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {event.startTime || '--:--'} {event.endTime ? `- ${event.endTime}` : ''} Uhr
                                </Typography>
                                {isEditable && <EditButton onClick={() => setEditingField('time')} />}
                            </Box>
                        )}

                        {!isMultiDay && isEditable && editingField === 'time' && (
                            <div className="my-2 space-y-2" onClick={(e) => e.stopPropagation()}>
                                <InlineFieldEditor 
                                    label="Startzeit" 
                                    type="time"
                                    value={event.startTime || ''} 
                                    onSave={(val) => { handleUpdateField('startTime', val); setEditingField(null); }}
                                    onCancel={() => setEditingField(null)}
                                />
                                <InlineFieldEditor 
                                    label="Endzeit" 
                                    type="time"
                                    value={event.endTime || ''} 
                                    onSave={(val) => { handleUpdateField('endTime', val); setEditingField(null); }}
                                    onCancel={() => setEditingField(null)}
                                />
                            </div>
                        )}

                        {(event.location || isEditable) && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: 'rgba(var(--color-surface-on-variant), 0.85)' }}>
                                <Box sx={{ bgcolor: 'rgba(var(--color-surface-on-variant), 0.08)', p: 0.7, borderRadius: 1.5, display: 'flex' }}>
                                    <LocationIcon sx={{ fontSize: 16 }} />
                                </Box>
                                <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>{event.location || 'Kein Ort angegeben'}</Typography>
                                {isEditable && <EditButton onClick={() => setEditingField('location')} />}
                            </Box>
                        )}

                        {isEditable && editingField === 'location' && (
                            <div className="my-2" onClick={(e) => e.stopPropagation()}>
                                <InlineFieldEditor 
                                    label="Ort" 
                                    value={event.location || ''} 
                                    onSave={(val) => { handleUpdateField('location', val); setEditingField(null); }}
                                    onCancel={() => setEditingField(null)}
                                />
                            </div>
                        )}

                        {isEditable && editingField === 'category' && (
                            <div className="my-2" onClick={(e) => e.stopPropagation()}>
                                <div className="p-4 rounded-2xl bg-surface-container border border-primary/20">
                                    <label className="text-label-medium font-bold text-primary uppercase block mb-2">Kategorie</label>
                                    <select 
                                        value={event.category}
                                        onChange={(e) => { handleUpdateField('category', e.target.value); setEditingField(null); }}
                                        className="w-full bg-surface-containerLowest border border-outlineVariant/30 rounded-xl px-4 py-3"
                                    >
                                        <option value="Gruppenstunde">Gruppenstunde</option>
                                        <option value="Lager">Lager</option>
                                        <option value="Aktion">Aktion</option>
                                        <option value="Sonstiges">Sonstiges</option>
                                    </select>
                                </div>
                            </div>
                        )}

                        {/* Recaps and Action footer */}
                        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgb(var(--color-outline-variant))', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }} onClick={(e) => e.stopPropagation()}>
                            {event.link ? (
                                <div className="flex items-center gap-2 flex-wrap">
                                    <Button
                                        variant="contained"
                                        color={categoryColor}
                                        size="small"
                                        href={ensureAbsoluteUrl(event.link)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        endIcon={<ExternalLinkIcon sx={{ fontSize: 16 }} />}
                                        sx={{
                                            borderRadius: 100,
                                            py: 0.9,
                                            px: 2.2,
                                            fontWeight: 700,
                                            textTransform: 'none',
                                            fontSize: '0.8rem',
                                            boxShadow: 'none',
                                            bgcolor: 'rgb(var(--color-primary))',
                                            color: 'rgb(var(--color-primary-on))',
                                            '&:hover': {
                                                bgcolor: 'rgb(var(--color-primary))',
                                                boxShadow: 'none',
                                                filter: 'brightness(1.08)'
                                            }
                                        }}
                                    >
                                        {getExternalButtonLabel()}
                                    </Button>
                                    {event.slug && (
                                        <Button
                                            variant="text"
                                            size="small"
                                            onClick={() => navigate(`/events/${event.slug}`)}
                                            sx={{
                                                borderRadius: 100,
                                                py: 0.8,
                                                px: 1.8,
                                                fontWeight: 700,
                                                textTransform: 'none',
                                                fontSize: '0.8rem',
                                                color: 'primary.main',
                                                '&:hover': {
                                                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                                                    color: 'primary.main'
                                                }
                                            }}
                                        >
                                            Mehr erfahren
                                        </Button>
                                    )}
                                </div>
                            ) : event.slug ? (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    size="small"
                                    onClick={() => navigate(`/events/${event.slug}`)}
                                    endIcon={<ArrowRightIcon sx={{ fontSize: 16 }} />}
                                    sx={{
                                        borderRadius: 100,
                                        py: 0.9,
                                        px: 2.2,
                                        fontWeight: 700,
                                        textTransform: 'none',
                                        fontSize: '0.8rem',
                                        boxShadow: 'none',
                                        bgcolor: 'rgb(var(--color-primary))',
                                        color: 'rgb(var(--color-primary-on))',
                                        '&:hover': {
                                            bgcolor: 'rgb(var(--color-primary))',
                                            boxShadow: 'none',
                                            filter: 'brightness(1.08)'
                                        }
                                    }}
                                >
                                    {getInternalButtonLabel()}
                                </Button>
                            ) : (
                                <Button
                                    variant="text"
                                    size="small"
                                    onClick={() => setIsExpanded(true)}
                                    sx={{ fontWeight: 700, textTransform: 'none', fontSize: '0.8rem' }}
                                >
                                    Details ansehen
                                </Button>
                            )}

                            {/* Linked blog post quick badge if available */}
                            {event.linkedBlogs && event.linkedBlogs.length > 0 && (
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="inherit"
                                    onClick={() => navigate(`/blog/${event.linkedBlogs[0]._id}`)}
                                    startIcon={<ArticleIcon sx={{ fontSize: 16 }} />}
                                    sx={{
                                        borderRadius: 100,
                                        py: 0.6,
                                        px: 1.5,
                                        fontSize: '0.72rem',
                                        fontWeight: 700,
                                        textTransform: 'none',
                                        borderColor: 'rgba(var(--color-outline), 0.4)'
                                    }}
                                >
                                    Bericht
                                </Button>
                            )}
                        </Box>
                    </Stack>
                </CardContent>
            </Card>

            {/* Calendar Popover Menu */}
            <Menu
                anchorEl={calendarAnchorEl}
                open={Boolean(calendarAnchorEl)}
                onClose={handleCalendarClose}
                onClick={(e) => e.stopPropagation()}
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        boxShadow: 'none',
                        bgcolor: 'rgb(var(--color-surface-container-high))',
                        border: '1px solid rgb(var(--color-outline-variant))',
                        p: 0.5
                    }
                }}
            >
                <MenuItem onClick={handleDownloadIcs} sx={{ borderRadius: 2, gap: 1.5, py: 1 }}>
                    <ListItemIcon sx={{ minWidth: 32, color: 'primary.main' }}>
                        <DownloadIcon sx={{ fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText primary="Apple / Outlook (.ics)" primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }} />
                </MenuItem>
                <MenuItem onClick={handleOpenGoogleCal} sx={{ borderRadius: 2, gap: 1.5, py: 1 }}>
                    <ListItemIcon sx={{ minWidth: 32, color: 'secondary.main' }}>
                        <ExternalLinkIcon sx={{ fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText primary="Google Kalender" primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }} />
                </MenuItem>
            </Menu>

            {/* Expanded Dialog View */}
            <Dialog
                open={isExpanded}
                onClose={() => setIsExpanded(false)}
                maxWidth="sm"
                fullWidth
                TransitionProps={{ mountOnEnter: true, unmountOnExit: true }}
                PaperProps={{
                    sx: {
                        borderRadius: 6,
                        backgroundImage: 'none',
                        bgcolor: 'rgb(var(--color-surface-container-highest))',
                        border: '1px solid rgb(var(--color-outline-variant))',
                        boxShadow: 'none',
                        position: 'relative',
                        overflow: 'hidden'
                    }
                }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 3.5, pb: 1, px: 4 }}>
                    <span className={`px-3 py-1 rounded-lg text-xs font-bold uppercase tracking-wider ${categoryStyles.badgeClass}`}>
                        {event.category || 'Event'}
                    </span>
                    <IconButton
                        onClick={() => setIsExpanded(false)}
                        sx={{
                            bgcolor: 'rgba(var(--color-surface-on-variant), 0.05)',
                            '&:hover': { bgcolor: 'rgba(var(--color-surface-on-variant), 0.1)' }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ px: 4, pb: 2 }}>
                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: 800,
                            color: 'rgb(var(--color-surface-on))',
                            mb: 3,
                            lineHeight: 1.2
                        }}
                    >
                        {event.title}
                    </Typography>

                    <Stack spacing={2.5} sx={{ mb: 4 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
                            <Box sx={{ bgcolor: categoryStyles.containerVar, color: categoryStyles.onContainerVar, p: 1.2, borderRadius: 2, display: 'flex' }}>
                                <CalendarIcon sx={{ fontSize: 24, color: 'inherit' }} />
                            </Box>
                            <Box>
                                <Typography variant="caption" sx={{ color: 'rgba(var(--color-surface-on-variant), 0.6)', fontWeight: 600, textTransform: 'uppercase', display: 'block' }}>Datum</Typography>
                                <Typography variant="body1" sx={{ fontWeight: 600 }}>{formatDate(event.date, event.endDate)}</Typography>
                            </Box>
                        </Box>

                        {!isMultiDay && (event.startTime || event.endTime) && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
                                <Box sx={{ bgcolor: 'rgba(var(--color-surface-on-variant), 0.05)', p: 1.2, borderRadius: 2, display: 'flex' }}>
                                    <TimeIcon sx={{ fontSize: 24, color: 'rgba(var(--color-surface-on-variant), 0.8)' }} />
                                </Box>
                                <Box>
                                    <Typography variant="caption" sx={{ color: 'rgba(var(--color-surface-on-variant), 0.6)', fontWeight: 600, textTransform: 'uppercase', display: 'block' }}>Zeit</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                        {event.startTime} {event.endTime ? `- ${event.endTime}` : ''} Uhr
                                    </Typography>
                                </Box>
                            </Box>
                        )}

                        {event.location && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
                                <Box sx={{ bgcolor: 'rgba(var(--color-surface-on-variant), 0.05)', p: 1.2, borderRadius: 2, display: 'flex' }}>
                                    <LocationIcon sx={{ fontSize: 24, color: 'rgba(var(--color-surface-on-variant), 0.8)' }} />
                                </Box>
                                <Box>
                                    <Typography variant="caption" sx={{ color: 'rgba(var(--color-surface-on-variant), 0.6)', fontWeight: 600, textTransform: 'uppercase', display: 'block' }}>Ort</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{event.location}</Typography>
                                </Box>
                            </Box>
                        )}
                    </Stack>

                    {event.description && (
                        <Box
                            sx={{
                                bgcolor: 'rgba(var(--color-surface-container), 0.5)',
                                p: 3,
                                borderRadius: 4,
                                mb: 2,
                                border: '1px solid rgb(var(--color-outline-variant))'
                            }}
                        >
                            <Typography variant="body1" sx={{ whiteSpace: 'pre-line', color: 'rgba(var(--color-surface-on), 0.9)', lineHeight: 1.7 }}>
                                {event.description}
                            </Typography>
                        </Box>
                    )}
                </DialogContent>

                <DialogActions sx={{ p: 4, pt: 1, flexDirection: 'column', gap: 2 }}>
                    <div className="flex w-full gap-3">
                        {event.link ? (
                            <div className="flex flex-col w-full gap-2">
                                <Button
                                    href={ensureAbsoluteUrl(event.link)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    variant="contained"
                                    color="primary"
                                    fullWidth
                                    size="large"
                                    endIcon={<ExternalLinkIcon sx={{ fontSize: 20 }} />}
                                    sx={{
                                        borderRadius: 100,
                                        py: 1.8,
                                        fontWeight: 700,
                                        textTransform: 'none',
                                        fontSize: '1rem',
                                        boxShadow: 'none',
                                        bgcolor: 'rgb(var(--color-primary))',
                                        color: 'rgb(var(--color-primary-on))',
                                        '&:hover': {
                                            bgcolor: 'rgb(var(--color-primary))',
                                            boxShadow: 'none',
                                            filter: 'brightness(1.08)'
                                        }
                                    }}
                                >
                                    {getExternalButtonLabel()}
                                </Button>
                                {event.slug && (
                                    <Button
                                        onClick={() => { setIsExpanded(false); navigate(`/events/${event.slug}`); }}
                                        variant="outlined"
                                        fullWidth
                                        size="large"
                                        endIcon={<ArrowRightIcon sx={{ fontSize: 20 }} />}
                                        sx={{
                                            borderRadius: 100,
                                            py: 1.5,
                                            fontWeight: 700,
                                            textTransform: 'none',
                                            fontSize: '0.95rem',
                                            color: 'primary.main',
                                            borderColor: 'rgba(var(--color-outline), 0.4)',
                                            '&:hover': {
                                                borderColor: 'primary.main',
                                                bgcolor: 'rgba(var(--color-primary-container), 0.5)'
                                            }
                                        }}
                                    >
                                        Zur Event-Seite (Mehr Infos)
                                    </Button>
                                )}
                            </div>
                        ) : event.slug && (
                            <Button
                                onClick={() => { setIsExpanded(false); navigate(`/events/${event.slug}`); }}
                                variant="contained"
                                color="primary"
                                fullWidth
                                size="large"
                                endIcon={<ArrowRightIcon sx={{ fontSize: 20 }} />}
                                sx={{
                                    borderRadius: 100,
                                    py: 1.8,
                                    fontWeight: 700,
                                    textTransform: 'none',
                                    fontSize: '1rem',
                                    boxShadow: 'none',
                                    bgcolor: 'rgb(var(--color-primary))',
                                    color: 'rgb(var(--color-primary-on))',
                                    '&:hover': {
                                        bgcolor: 'rgb(var(--color-primary))',
                                        boxShadow: 'none',
                                        filter: 'brightness(1.08)'
                                    }
                                }}
                            >
                                {getInternalButtonLabel()}
                            </Button>
                        )}

                        <Button
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const url = `${window.location.origin}/events/${event.slug || ''}`;
                                if (navigator.share) {
                                    navigator.share({
                                        title: event.title,
                                        url: url
                                    }).catch(console.error);
                                } else {
                                    navigator.clipboard.writeText(url);
                                    alert('Link kopiert!');
                                }
                            }}
                            variant="outlined"
                            startIcon={<ShareIcon />}
                            sx={{
                                borderRadius: 100,
                                minWidth: '120px',
                                border: '1.5px solid rgba(var(--color-outline), 0.3)',
                                color: 'rgba(var(--color-surface-on), 0.8)',
                                fontWeight: 600,
                                textTransform: 'none'
                            }}
                        >
                            Teilen
                        </Button>
                    </div>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default EventCard;
